import { useState, useMemo, createContext, useContext } from "react";

import { createTheme, ThemeProvider, responsiveFontSizes, lighten } from '@mui/material/styles';

import { grey, teal, red, pink, orange} from '@mui/material/colors';
import ContextTypes from "../Interfaces/context.interface";
import { ContentPageColor } from "../Hooks/GeneralFunc";
import { CssBaseline, useMediaQuery } from "@mui/material";
import { useDir } from "./DirectionContext";
import { ACCENT, ACCENT_HOVER } from "../Constants/accent";
import { CONTROL_RADIUS, DashTokens, dashTokens } from "../Constants/designTokens";
import { buildComponents, buttonVariants, paperVariants } from "../Constants/themeComponents";

declare module '@mui/material/styles' {
  interface Palette {
    dash: DashTokens;
  }
  interface PaletteOptions {
    dash?: DashTokens;
  }
}

const MODE_STORAGE_KEY = 'mode';

const styleOptions = {
  main: "#000000",
  black: "rgb(18,18,18)",
  white: "#f7f7f7",
  borderRadius: CONTROL_RADIUS,
  buttonBR: CONTROL_RADIUS,
  textFieldBR: CONTROL_RADIUS,
  light: {
    primary: ACCENT,
    hover: ACCENT_HOVER,
    contrastText: "#f7f7f7",
    secondary: grey[300],
    secondaryText: "#181818"
  },
  dark: {
    primary: ACCENT,
    hover: ACCENT_HOVER,
    contrastText: "#ffffff",
    secondary: grey[800],
  }
};

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

export const useColorMode = () => useContext(ColorModeContext);

export default function ToggleColorMode({children}: ContextTypes['childrenProps']) {

    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    const initialMode: 'light' | 'dark' = stored === 'dark' ? 'dark' : 'light';
    const [mode, setMode] = useState<any>(initialMode);
    const sm = useMediaQuery('(max-width:600px)');
    const { language } = useDir();
    const dir: 'rtl' | 'ltr' =
      language === 'ar' || language === 'kr' ? 'rtl' : 'ltr';

    ContentPageColor(initialMode === 'light' ? styleOptions.white : styleOptions.black);

    const colorMode = useMemo(
      () => ({
        toggleColorMode: () => {
          setMode((prevMode: string) => {
            if(prevMode === "light") {
              ContentPageColor(styleOptions.black);
              localStorage.setItem(MODE_STORAGE_KEY, 'dark');
              return 'dark'
            } else {
              ContentPageColor(styleOptions.white);
              localStorage.setItem(MODE_STORAGE_KEY, 'light');
              return 'light'
            }
          });
        },
      }),
      [],
    );
  
    let theme = useMemo(
      () =>
        createTheme({
          typography: {
            fontFamily: 'Roboto, Almarai',
            h1: {
              fontSize: sm ? "1.2rem" : "1.3rem",
              fontWeight: 500
            },
            h2: {
              fontSize: sm ? "1.1rem" : "1.2rem",
              fontWeight: 450
            },
            h3: {
              fontSize: sm ? "1rem" : "1.1rem",
              fontWeight: 440
            },
            h4:{
              fontSize: sm ? "0.95rem" : "1.05rem",
              fontWeight: 430
            },
            h5: {
              fontSize: sm ? "0.9rem" : "1rem",
              fontWeight: 420
            },
            h6: {
              fontSize: sm ? "0.85rem" : "0.95rem",
              fontWeight: 410
            },
            subtitle1: {
              fontSize: sm ? "0.80rem" : "0.9rem",
              fontWeight: 410
            },
            subtitle2: {
              fontSize: sm ? "0.75rem" : "0.85rem",
              fontWeight: 410
            },
            body1: {
              fontSize: sm ? "0.75rem" : "0.8rem",
              fontWeight: 400
            },
            body2: {
              fontSize: sm ? "0.65rem" : "0.75rem",
              fontWeight: 400
            },
            caption: {
              fontSize: sm ? "0.55rem" : "0.65rem"
            }
          },
          components: {
            ...buildComponents(mode === 'dark'),
            MuiButton: {
              variants: buttonVariants(mode === 'dark'),
              styleOverrides: {
                root: ({ ownerState }) => ({
                  ...(ownerState.variant === 'contained' ?
                     {
                      ":hover": {
                        backgroundColor: 
                        ownerState.color === 'primary' ? mode === 'light' ? styleOptions.light.hover : styleOptions.dark.hover
                        : 
                        ownerState.color === 'secondary' ? mode === 'light' ? grey[300] : grey[700]
                        :
                        ownerState.color === 'success' ? mode === 'light' ? teal[300] : lighten(teal[300], 0.1)
                        :
                        ownerState.color === 'error' ? mode === 'light' ? red[500] : lighten(red[400], 0.1)
                        :
                        ownerState.color === 'warning' ? mode === 'light' ? lighten(orange[800], 0.1) : lighten(orange[700], 0.1)
                        :
                        ownerState.color === 'info' && lighten(pink[400], 0.1)
                      },
                      borderRadius: styleOptions.buttonBR,
                      textTransform: 'none'
                    }
                    :
                    ownerState.variant === 'outlined' ? 
                    {
                      ":hover": {
                        backgroundColor: 
                        ownerState.color === 'primary' ? styleOptions.light.primary
                        :
                        ownerState.color === 'secondary' ? mode === 'light' ? styleOptions.light.secondary : grey[800]
                        :
                        ownerState.color === 'success' ? mode === 'light' ? teal[300] : lighten(teal[300], 0.1)
                        :
                        ownerState.color === 'error' ? mode === 'light' ? red[400] : lighten(red[400], 0.1)
                        :
                        ownerState.color === 'warning' ? mode === 'light' ? lighten(orange[800], 0.1) : lighten(orange[700], 0.1)
                        :
                        ownerState.color === 'info' && lighten(pink[400], 0.1),

                        color: ownerState.color === 'secondary' ? mode === 'light' ? styleOptions.black : styleOptions.white : styleOptions.white
                      },
                      color: ownerState.color === 'secondary' ? (mode === 'light' ? grey[800] : grey[300]) : ownerState.color,
                      borderColor: ownerState.color === 'secondary' ? grey[500] : ownerState.color,
                      borderRadius: styleOptions.buttonBR,
                      textTransform: 'none'
                    }
                    : 
                    ownerState.variant === 'text' && 
                    {
                      ":hover": {
                        color: 
                        ownerState.color === 'primary' ? ACCENT_HOVER
                        :
                        ownerState.color === 'secondary' ? mode === 'light' ? grey[800] : styleOptions.dark.contrastText
                        :
                        ownerState.color === 'success' ? mode === 'light' ? teal[700] : teal[200]
                        :
                        ownerState.color === 'error' ? mode === 'light' ? red[700] : red[200]
                        :
                        ownerState.color === 'warning' ? mode === 'light' ? orange[900] : orange[200]
                        :
                        ownerState.color === 'info' && mode === 'light' ? pink[700] : pink[200]
                        ,
                        backgroundColor: "rgba(0,0,0, 0)"
                      },
                      color: ownerState.color === 'secondary' ? (mode === 'light' ? grey[600] : grey[400]) : ownerState.color,
                      textTransform: 'none'
                    }
                    ),
                }),
              },
            },
            MuiTextField: {
              styleOverrides: {
                root: ({ownerState}) => ({ //ownerState.variant === 'outlined' &&
                  ...( document.dir === 'rtl' ? {
                    'fieldset': {
                        borderRadius: styleOptions.textFieldBR,
                        textAlign: 'right',
                      },
                      outline: 'none',
                      '& label.Mui-focused': {
                        //right: 10
                        // marginRight: '1%'
                      },
                      'label': {
                        right: 30,
                        transformOrigin: "top right"

                      },
                      'p': {
                        textAlign: 'right',
                      },
                  }
                  :
                  {
                    'fieldset': {
                      borderRadius: styleOptions.textFieldBR,
                    },
                    outline: 'none',
                    'input': {
                      fontSize: sm ? "0.9rem" : "0.9rem",
                      height: sm ? "1em" : "1.2em",
                      borderRadius: styleOptions.textFieldBR
                    }
                  }
                  )
                }),
              },
            },
            MuiPaper: {
              variants: paperVariants(mode === 'dark'),
              styleOverrides: {
                root: ({ownerState}) => ({
                  ...({
                    borderRadius: styleOptions.borderRadius,
                    boxShadow: '-1px 0 15px 2px rgba(0,0,0,0.05)',
                    WebkitBoxShadow: '-1px 0 15px 2px rgba(0,0,0,0.05)',
                    MozBoxShadow: '-1px 15px 15px 2px rgba(0,0,0,0.05)',
                    WebkitBackdropFilter: 'blur(10px)',
                    backdropFilter: 'blur(10px)',
                  })
                })
              }
            },
            MuiAppBar: {
              styleOverrides: {
                root: ({ownerState}) => ({
                  ...({
                    boxShadow: '-1px 0 15px 2px rgba(0,0,0,0.02)',
                    WebkitBoxShadow: '-1px 0 15px 2px rgba(0,0,0,0.02)',
                    MozBoxShadow: '-1px 15px 15px 2px rgba(0,0,0,0.02)',
                    WebkitBackdropFilter: 'blur(10px)',
                    backdropFilter: 'blur(10px)',
                    backgroundColor: mode === 'light' ? 'rgba(245, 245, 245, 0.6)' : 'rgba(24, 24, 24, 0.8)',
                    borderBottom: '0.5px solid rgba(100,100,100,0.05)',
                  })
                })
              }
            },
            MuiSelect: {
              styleOverrides: {
                root: ({ownerState}) => ({
                  ...({
                    borderRadius: styleOptions.buttonBR,
                    '.MuiOutlinedInput-input': {
                      padding: "8px 10px",
                    }
                  })
                })
              }
            },
          },
          direction: dir,
          palette: {
            mode,
            dash: dashTokens(mode === 'dark'),
            ...(mode === 'light'
            ? {
                // palette values for light mode
                primary: {
                  main: styleOptions.light.primary,
                  contrastText: styleOptions.light.contrastText,
                },
                secondary: {
                  main: styleOptions.light.secondary,
                  contrastText: styleOptions.light.secondaryText,
                },
                text: {
                  primary: '#000',
                  secondary: grey[700]
                },
                background: {
                  default: '#f7f7f7',
                  paper: 'rgba(245, 245, 245, 0.6)',
                },
                success: {
                  main: teal[400],
                  contrastText: styleOptions.light.contrastText,
                },
                 error: {
                  main: red[500],
                  contrastText: styleOptions.light.contrastText,
                },
                 warning: {
                  main: orange[700],
                  contrastText: styleOptions.light.contrastText,
                },
                 info: {
                  main: pink[400],
                  contrastText: styleOptions.light.contrastText,
                }
              }
            : {
                // palette values for dark mode
                primary: {
                  main: styleOptions.dark.primary,
                  contrastText: styleOptions.dark.contrastText,
                },
                secondary: {
                  main: styleOptions.dark.secondary,
                  contrastText: styleOptions.dark.contrastText,
                },
                text: {
                  primary: '#f7f7f7',
                  secondary: grey[500]
                },
                background: {
                  default: styleOptions.black,
                  paper: 'rgba(10, 10, 10, 0.8)',
                },
                success: {
                  main: teal[300],
                  contrastText: styleOptions.dark.contrastText,
                },
                 error: {
                  main: red[400],
                  contrastText: styleOptions.dark.contrastText,
                },
                 warning: {
                  main: orange[500],
                  contrastText: styleOptions.dark.contrastText,
                },
                  info: {
                  main: pink[400],
                  contrastText: styleOptions.dark.contrastText,
                }
              }),
          },
        }),
      [mode, sm, dir],
    );
  theme = responsiveFontSizes(theme, {
    breakpoints: ["sm", "lg"],
    variants: ["h1"]
  })
    return (
        <ThemeProvider theme={theme}>
      <ColorModeContext.Provider value={colorMode}>
        <CssBaseline />
          {children}
      </ColorModeContext.Provider>
        </ThemeProvider>
    );
  }